import { z } from 'zod'
import { MAX_TEXT_LENGTH } from '../config'

/**
 * The formatted text a `Text` element may contain.
 *
 * This is a deliberately tiny subset of the ProseMirror document shape that
 * Tiptap produces, and the whitelist IS the sanitisation the brief asks for in
 * §9. Storing Tiptap's JSON and handing it to a generic JSON-to-HTML converter
 * would render whatever node types happen to be in the payload; parsing it
 * through this schema means an unknown node, an unknown mark or a hostile href
 * fails validation at the door instead of reaching a renderer.
 *
 * What is not here is as important as what is. No headings: those are their own
 * element with a fixed level, so an owner cannot bury an `h2` inside a paragraph
 * and wreck the page outline for search engines. No images, no tables, no raw
 * HTML, no inline styles, no colours, no font sizes.
 */

/** Only schemes a link may use. `javascript:` and `data:` are the reason. */
const SAFE_LINK = /^(https?:\/\/|mailto:|tel:)/i

export const richTextMarkSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bold') }),
  z.object({ type: z.literal('italic') }),
  z.object({
    type: z.literal('link'),
    attrs: z.object({
      href: z.string().min(1).max(2048).regex(SAFE_LINK, 'unsupported link'),
      /*
        Not configurable. Every link written inside prose opens in the same tab
        except where the renderer decides otherwise, and `rel` is set by the
        renderer, never by the document. A document that could ask for
        `rel=""` on an external link is a document that can leak the referrer
        and hand `window.opener` to another site.
      */
      title: z.string().max(200).optional()
    })
  })
])

export const richTextInlineSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    text: z.string().max(MAX_TEXT_LENGTH),
    marks: z.array(richTextMarkSchema).max(3).optional()
  }),
  z.object({ type: z.literal('hardBreak') })
])

export const richTextParagraphSchema = z.object({
  type: z.literal('paragraph'),
  content: z.array(richTextInlineSchema).max(500).optional()
})

export const richTextListItemSchema = z.object({
  type: z.literal('listItem'),
  content: z.array(richTextParagraphSchema).min(1).max(4)
})

export const richTextListSchema = z.object({
  type: z.enum(['bulletList', 'orderedList']),
  content: z.array(richTextListItemSchema).min(1).max(100)
})

export const richTextBlockSchema = z.union([
  richTextParagraphSchema,
  richTextListSchema
])

export const richTextDocSchema = z.object({
  type: z.literal('doc'),
  content: z.array(richTextBlockSchema).max(200)
})

export type RichTextMark = z.infer<typeof richTextMarkSchema>
export type RichTextInline = z.infer<typeof richTextInlineSchema>
export type RichTextParagraph = z.infer<typeof richTextParagraphSchema>
export type RichTextList = z.infer<typeof richTextListSchema>
export type RichTextBlock = z.infer<typeof richTextBlockSchema>
export type RichTextDoc = z.infer<typeof richTextDocSchema>

/**
 * The text without any formatting.
 *
 * Used for three things that all need to count characters rather than render
 * them: the length limit, the share of a language that is actually translated,
 * and the fallback for an SEO description nobody filled in.
 */
export function richTextToPlainText(doc: RichTextDoc): string {
  const fromInline = (nodes: RichTextInline[] | undefined): string =>
    (nodes ?? [])
      .map(node => (node.type === 'text' ? node.text : ' '))
      .join('')

  return doc.content
    .map(block => {
      if (block.type === 'paragraph') return fromInline(block.content)
      return block.content
        .map(item => item.content.map(p => fromInline(p.content)).join(' '))
        .join(' ')
    })
    .join('\n')
    .trim()
}

/** An empty document, which is what a new text element starts as. */
export const emptyRichText: RichTextDoc = { type: 'doc', content: [] }
